import { MyIcon } from '~/components/ui/MyIcon'
import { MyImage } from '~/components/ui/MyImage'
import { usePostSocial } from '~/hooks/usePostSocial'
import classNames from 'classnames'
import { useMemo } from 'react'
import { digitalUnit } from '~/utils'
import Link from 'next/link'
import {
  PostTypes,
  VisibleType,
  SquarePostScope,
  SquarePostType,
  SquarePostStatus,
  SuperType,
} from '~/graphqls/types'
import { MyAvatar } from '../../ui/MyAvatar'
import { ImageLoader } from './SinglePostLoad'
import styles from './index.module.css'
import Router from 'next/router'
import CoverDom, { ICoverDomData } from './CoverDom'
import { useStores } from '~/hooks/useStores'
import { CDNImage } from '../CDNImage'
import { WhiteUserFrontControl } from '~/components/pages/plaza/WhiteUserFrontControl'
import AgainButton from './AgainButton'

const TEXT_LIMIT = 134

export enum PostScene {
  /** 图片适应高度，展示收藏按钮 */
  RESPONSIVE_SOCIAL = 'RESPONSIVE_SOCIAL',
  /**图片适应高度 */
  RESPONSIVE_NORMAL = 'RESPONSIVE_NORMAL',
  /**图片适应高度, 没有标题的方形 */
  RESPONSIVE_SQUARE = 'RESPONSIVE_SQUARE',
  /**图片方形 */
  SQUARE = 'SQUARE',
  /** 首页专用，显示同款数据，点击可以一键同款, 外部帖子直接展示为方格 */
  SAME_AGAIN = 'SAME_AGAIN',
}

export const SinglePost = ({
  post,
  scene = PostScene.SQUARE,
  param,
  offset,
  loading = false,
  onActionComplete,
  showSticky,
  showProcessingTag = false,
  isHome = false,
}: {
  post: PostTypes & { competitionStyle?: number | null }
  scene?: PostScene
  param?: { param: string; indexurl: string }
  offset?: number
  loading?: boolean
  onActionComplete?: (params?: { type?: string; id?: any }) => void
  showSticky?: boolean
  showProcessingTag?: boolean // 显示待审核标签
  isHome?: boolean
}) => {
  const {
    liked,
    likeCount,
    handleLike,
    faved,
    handleFav,
    canSameAgain,
    handleSameAgain,
    handleSearch,
  } = usePostSocial(post, {
    onActionComplete,
  })
  const { store } = useStores()
  const isSelf = store.userinfo?.base?.uid === post.user?.uid
  const showProcessing =
    isSelf && showProcessingTag && post?.status === SquarePostStatus.WAIT

  const percentage = useMemo(() => {
    if (post.type === SquarePostType.AI_COLLECTION) {
      return (300 / 220) * 100
    }
    if (scene === PostScene.SQUARE) return 100
    if (!post?.coverImage?.width || !post?.coverImage.height) return 100
    return (
      (Number(post?.coverImage?.height) / Number(post?.coverImage?.width)) * 100
    )
  }, [])

  const url = useMemo(() => {
    // 1.合集
    if (post.type === SquarePostType.AI_COLLECTION) {
      return `/collection/${post.id}`
    }
    // 2.笔记
    if (post.type === SquarePostType.NOTE) {
      return `/note/${post.id}`
    }
    // 3.ai创作
    if (!!param) {
      return `/plaza/${post.id}?param=${param.param}&offset=${offset}&indexurl=${param.indexurl}`
    }
    return `/plaza/${post.id}`
  }, [param, offset, post])

  // 纯文本
  const onlyText = !post?.coverImage?.imageUrl

  const _handleSameAgain = () => {
    // 一键同款付积分，跳转到帖子详情
    if (
      post?.visibleType !== VisibleType.CREATE_LIKEWISE_COST &&
      canSameAgain
    ) {
      handleSameAgain()
    } else {
      Router.push(url)
    }
  }

  return (
    <div className="w-full relative">
      <div
        className={classNames(
          'w-full h-0 relative overflow-hidden',
          styles.post,
          {
            'rounded-2xl':
              scene !== PostScene.SQUARE &&
              scene !== PostScene.RESPONSIVE_SQUARE,
            [styles.onlyTextWrap]: onlyText,
          }
        )}
        style={{
          paddingBottom: `${percentage}%`,
        }}
      >
        {!loading && (
          <div className="absolute left-0 top-0 bottom-0 right-0">
            <Link href={url} className="h-full">
              {post.type === SquarePostType.AI_COLLECTION ? (
                <CoverDom data={post?.aiCollection as ICoverDomData} />
              ) : onlyText ? (
                <div className={styles.onlyText}>
                  <div
                    className={classNames(
                      'text-overflow-line-2',
                      styles.onlyTextTitle
                    )}
                  >
                    {post?.title}
                  </div>
                  {!!post?.content && (
                    <div className={styles.onlyTextContent}>
                      {post?.content?.length > TEXT_LIMIT
                        ? (post?.content || '').substring(0, TEXT_LIMIT) + '...'
                        : post?.content}
                    </div>
                  )}
                </div>
              ) : (
                <MyImage
                  src={post?.coverImage?.imageUrl}
                  tag="v800"
                  className="w-full h-full"
                  alt={post?.title || ''}
                  dark
                />
              )}
              <div
                className={classNames(
                  'flex justify-start items-center absolute left-2 top-2 right-2',
                  styles.tags
                )}
              >
                {showProcessing ? (
                  <div className="p-1 rounded text-xs leading-[12px] text-neutral-800 transform scale-90 bg-[#4BC9F0]">
                    待审核
                  </div>
                ) : (
                  <>
                    {showSticky && (
                      <CDNImage
                        src="/images/icons/top-sticky.png"
                        className="w-6 h-6 rounded-full mr-2"
                        alt="top"
                      />
                    )}
                    {post.aiArtworkInfo?.superType === SuperType.PREFINE && (
                      <div
                        className="text-xs leading-none text-neutral-900 font-medium px-2.5 py-2 rounded-full mr-2"
                        style={{
                          background: `linear-gradient(269.96deg, #7851EC -22.07%, #FFC56F 131.97%)`,
                        }}
                      >
                        精绘
                      </div>
                    )}
                  </>
                )}
                {!!post.competitionStyle && (
                  <div className="flex justify-center items-center absolute top-0 right-0 min-w-[60px] font-semibold text-neutral-900 text-xs leading-none py-1.5 whitespace-nowrap bg-neutral-50/[0.56] rounded-[14px]">
                    <CDNImage
                      src="/images/icons/icon-vector.png"
                      className="w-2 block"
                      alt="vector"
                    />
                    <span className="px-[3px]">No.{post.competitionStyle}</span>
                    <CDNImage
                      src="/images/icons/icon-vector.png"
                      className="w-2 block rotate-180"
                      alt="vector"
                    />
                  </div>
                )}
                {post.competitionStyle === 0 && (
                  <div className="absolute top-0 right-0 font-semibold text-neutral-900 text-xs leading-none p-1.5 whitespace-nowrap bg-neutral-50/[0.56] rounded-[14px]">
                    参赛作品
                  </div>
                )}
              </div>

              {post?.imageList && post?.imageList?.length > 1 && (
                <div
                  className={classNames(
                    'absolute w-6 h-6 rounded-full bg-neutral-50/50 left-2 bottom-2 flex items-center justify-center',
                    styles.imageMul
                  )}
                >
                  <MyIcon
                    icon="image-stack"
                    className="text-base text-neutral-600"
                  />
                </div>
              )}

              {post?.aiArtworkInfo?.chatGptOptimize && (
                <div className="absolute right-2 bottom-2 rounded-full bg-neutral-50/50 w-6 h-6 pl-[2px]">
                  <CDNImage
                    src="/images/ai/chat_gpt.svg"
                    className="w-5 h-5"
                    alt="chatGpt"
                  />
                </div>
              )}

              {/* 方块展示标题，其他展示描述 */}
              {(scene == PostScene.SQUARE ||
                scene == PostScene.RESPONSIVE_SQUARE) &&
                post?.title && (
                  <div className={styles.description}>
                    <div>{post?.title}</div>
                  </div>
                )}
              {!!post?.aiArtworkInfo?.prompt &&
                scene !== PostScene.SQUARE &&
                scene !== PostScene.RESPONSIVE_SQUARE && (
                  <div className={styles.description}>
                    <div>{post?.aiArtworkInfo?.prompt}</div>
                  </div>
                )}
              {/* 合集 */}
              {!!post.aiCollection && !!post?.content && (
                <div className={styles.description}>
                  <div>{post?.content}</div>
                </div>
              )}
            </Link>
            <div className={styles.operateWrap}>
              <div className="flex">
                {/* <div
                  className={classNames(styles.operate, 'mr-2')}
                  onClick={handleSearch}
                >
                  <MyIcon
                    icon="search"
                    className="text-[20px] text-neutral-600"
                  />
                </div> */}
                <div
                  className={styles.operate}
                  onClick={handleLike}
                  title="点赞"
                >
                  <MyIcon
                    icon={liked ? 'heart-filled' : 'heart'}
                    className={classNames('text-[20px]', [
                      liked ? 'text-error' : 'text-neutral-600',
                    ])}
                  />
                </div>
              </div>
              <div className="flex">
                {scene === PostScene.RESPONSIVE_SOCIAL && (
                  <div
                    className={classNames(styles.operate, 'mr-2')}
                    onClick={handleFav}
                    title="收藏"
                  >
                    <MyIcon
                      icon={faved ? 'star-filled' : 'star'}
                      className={classNames('text-[20px]', [
                        faved ? 'text-[#FFD600]' : 'text-neutral-600',
                      ])}
                    />
                  </div>
                )}

                {post.type === SquarePostType.AI_ARTWORK && (
                  <>
                    <WhiteUserFrontControl
                      post={post}
                      isSquare
                      className={classNames(
                        styles.operate,
                        'text-neutral-900 text-large',
                        {
                          ['mr-2']: !isHome,
                        }
                      )}
                    />
                    {!isHome && (
                      <div
                        className={styles.operate}
                        onClick={_handleSameAgain}
                        title="一键同款"
                      >
                        <MyIcon
                          icon="hand-pointer"
                          className="text-[20px] text-neutral-600"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {scene !== PostScene.SQUARE &&
        scene !== PostScene.RESPONSIVE_SQUARE &&
        scene !== PostScene.SAME_AGAIN &&
        post.postScope === SquarePostScope.INSIDE && (
          <div
            className={classNames('mt-2 relative', {
              'opacity-0': loading,
              'mt-0': onlyText,
            })}
          >
            {!onlyText && (
              <div className="text-sm text-neutral-500 line-clamp-1">
                <Link href={url}>{post.title}</Link>
              </div>
            )}
            <div
              className={classNames('flex mt-1 justify-between items-center', {
                'mt-0': onlyText,
              })}
            >
              <div className="flex items-center">
                <MyAvatar
                  className="shrink-0"
                  image={post?.user?.avatarUrl as string}
                  userRole={post?.user?.userRoleInfo}
                  size={16}
                  hiddenIcon
                />
                <span className="text-neutral-400 text-xs max-w-[80%] line-clamp-1 ml-1">
                  {post?.user?.nickname}
                </span>
              </div>
              <div
                className={classNames('text-xs flex items-center', [
                  liked ? 'text-error' : 'text-neutral-400',
                ])}
              >
                <MyIcon
                  className="text-sm mr-0.5"
                  icon={liked ? 'heart-filled' : 'heart'}
                />
                <span>{digitalUnit(likeCount)}</span>
              </div>
            </div>
          </div>
        )}

      {/* 一件同款专用 */}
      {scene === PostScene.SAME_AGAIN && post.user && (
        <div className={classNames('mt-2 relative', { 'opacity-0': loading })}>
          <div className="text-sm text-neutral-500 line-clamp-1">
            <Link href={url}>{post.title}</Link>
          </div>
          <div className="flex mt-1 justify-between items-center">
            <div className="flex items-center">
              <MyAvatar
                className="shrink-0"
                image={post?.user?.avatarUrl as string}
                userRole={post?.user?.userRoleInfo}
                size={16}
                hiddenIcon
              ></MyAvatar>
              <span className="text-neutral-400 text-xs max-w-[80%] line-clamp-1 ml-1">
                {post?.user?.nickname}
              </span>
            </div>

            {post.type !== SquarePostType.AI_COLLECTION && (
              <AgainButton post={post} />
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute top-0 left-0 right-0 bottom-0">
          <ImageLoader />
        </div>
      )}
    </div>
  )
}
